/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

//LDS Buybox Module refers to PDP or Product Display Page.

// import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps
} from '@msdyn365-commerce-modules/buybox/src/modules/buybox/../../common';
import { IBuyboxFindInStoreViewProps } from '@msdyn365-commerce-modules/buybox/src/modules/buybox/./components/buybox-find-in-store';
import { configure } from 'mobx';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { IBuyboxViewProps } from './lds-buybox';
// @ts-ignore
import { ExternalLink } from '@churchofjesuschrist/eden-icons';
import { ILdsBuyboxResources } from './lds-buybox.props.autogenerated';
import { formatString } from '../../helpers/utilities/string-utilities';
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';

//@ts-ignore
import { Error } from '@churchofjesuschrist/eden-alert';
import _ from 'lodash';

configure({ isolateGlobalState: true });

const BuyboxView: React.FC<IBuyboxViewProps> = props => {
    const {
        resources,
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        unitOfMeasure,
        productSpecificationResult,
        isSubscription,
        isManualSubscription,
        setSubscriptionAccountNumber
    } = props;
    const [type, setType] = React.useState<any>();
    const [sizingChart, setSizingChart] = React.useState<any>(false);
    const [count, setCount] = React.useState<any>(0);
    const [errorOpen, setErrorOpen] = React.useState<any>(true);
    const getCartData = async () => {
        const cartStateData: ICartState = await getCartState(props.context.actionContext);
        cartStateData.cart.CartLines?.map((cart: any) => {
            setType(cart.DeliveryMode);
        });
    };
    React.useEffect(() => {
        void getCartData();
    }, []);
    React.useEffect(() => {
        const isSizingChart = sessionStorage.getItem('sizingChart');

        if (isSizingChart && isSizingChart === 'true' && sizingChart === false) {
            setSizingChart(true);
        } else if (isSizingChart && isSizingChart === 'false' && sizingChart === true) {
            setSizingChart(false);
        }
    }, [props]);

    const isProductPurchaseEnabled = isPurchaseEnabled(productSpecificationResult);
    const deliveryOptions = props.data.deliveryOptions.result?.DeliveryOptions;
    let showAddToCartButton = true;

    if (deliveryOptions && deliveryOptions.length > 0) {
        const isOnlyBopis = deliveryOptions[0].ExtensionProperties?.find(y => y.Key === 'isOnlyBOPIS')?.Value?.BooleanValue;
        showAddToCartButton = !isOnlyBopis;
    }

    let productAuth;
    if (props.data.productSpecificationData && props.data.productSpecificationData.result) {
        productAuth = props.data.productSpecificationData.result.filter((attribute: any) => {
            return attribute.Name === 'Endowment' || attribute.Name === 'Temple' || attribute.Name === 'Recommend';
        });
    }
    let authRequired;
    if (productAuth) {
        authRequired = productAuth.map((attribute: any) => {
            return attribute.TextValue.includes('YES') ? true : false;
        });
    }
    const USE_VIEW_RESTRICTIONS = process.env.USE_VIEW_RESTRICTIONS;

    if (parseInt(USE_VIEW_RESTRICTIONS || '', 10) === 1 && authRequired?.includes(true) && !props.context.request.user.isAuthenticated) {
        return <Error>{props.resources.authRequiredProduct}</Error>;
    } else {
        return (
            <Module {...ModuleProps}>
                <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
                <Node {...ProductInfoContainerProps}>
                    {/* @ts-ignore */}
                    <h1 className={title.props.className} dangerouslySetInnerHTML={{ __html: title.props.text }} />
                    {unitOfMeasure}
                    {/* @ts-ignore */}
                    <div className={description.props.className} dangerouslySetInnerHTML={{ __html: description.props.children }} />
                    {price}
                    {rating}
                    {configure && _renderConfigure(configure, productSpecificationResult, resources, sizingChart, count, setCount)}
                    {keyInPrice && _renderKeyInPrice(keyInPrice)}
                    {isProductPurchaseEnabled &&
                        quantity &&
                        _renderQuantity(quantity, findInStore?.addToCartQuantityError, errorOpen, setErrorOpen, props.buyboxQuantityError)}
                    {isProductPurchaseEnabled && (
                        <h2 style={{ marginTop: '30px', marginBottom: inventoryLabel ? '0px' : '15px' }}>{resources.addToCartText}</h2>
                    )}
                    {isProductPurchaseEnabled && (
                        <h3
                            className='add-to-cart-main-heading'
                            style={{
                                marginTop: '10px',
                                paddingBottom: isProductPurchaseEnabled && inventoryLabel ? '0px' : '10px'
                            }}
                        >
                            {resources.addToCartHeader}
                        </h3>
                    )}
                    {isProductPurchaseEnabled &&
                        inventoryLabel &&
                        // @ts-ignore
                        inventoryLabel.props &&
                        // @ts-ignore
                        inventoryLabel.props.children &&
                        // @ts-ignore
                        inventoryLabel.props.children.props &&
                        // @ts-ignore
                        inventoryLabel.props.children.props.children && (
                            <p className='add-to-cart-heading' style={{ paddingTop: '5px' }}>
                                {/* @ts-ignore */}
                                {inventoryLabel.props.children.props.children}
                            </p>
                        )}
                    {!isProductPurchaseEnabled && inventoryLabel}
                    {isSubscription && isManualSubscription && _renderSubscriptionRenewal(setSubscriptionAccountNumber, resources)}
                    {isProductPurchaseEnabled && _renderCheckoutActions(showAddToCartButton, addToCart, findInStore, type, resources)}
                    {addToOrderTemplate && _renderAddToOrderTemplate(addToOrderTemplate, resources)}
                    {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
                    {_renderMediaLinks(props)}
                </Node>
            </Module>
        );
    }
};

const _renderCheckoutActions = (
    showAddToCartButton: boolean,
    addToCart: IBuyboxAddToCartViewProps | null | undefined,
    findInStore: IBuyboxFindInStoreViewProps | null | undefined,
    type: string,
    resources: any
) => {
    // if (type === '' || type === 'MAG') {
    //     return <>{showAddToCartButton && addToCart && _renderAddToCart(addToCart, resources)}</>;
    // } else if (type === 'PICKUP' || type === 'Pickup' || type === 'Curbside' || type === 'CURBSIDE') {
    //     return <>{findInStore && _renderFindInStore(findInStore)}</>;
    // } else {
    //     return (
    //         <>
    //             {showAddToCartButton && addToCart && _renderAddToCart(addToCart, resources)}
    //             {findInStore && _renderFindInStore(findInStore)}
    //         </>
    //     );
    // }
    return (
        <>
            {showAddToCartButton && addToCart && _renderAddToCart(addToCart, resources)}
            {findInStore && _renderFindInStore(findInStore)}
        </>
    );
};

const _renderSubscriptionRenewal = (setSubscriptionAccountNumber: any, resources: ILdsBuyboxResources) => {
    const renewalBoxStyles = {
        padding: '35px 0 15px 0'
    };
    const renewalLabelStyles = {
        paddingBottom: '7px'
    };
    const renewalInputStyles = {
        fontSize: '20px',
        lineHeight: '25px'
    };
    return (
        <div style={renewalBoxStyles}>
            <div style={renewalLabelStyles}>{resources.subscriptionRenewalInputLabel}</div>
            <input style={renewalInputStyles} onChange={x => setSubscriptionAccountNumber(x.target.value)} />
        </div>
    );
};

const _renderMediaLinks = (props: IBuyboxViewProps) => {
    // @ts-ignore
    const productSpecificationData = props.data?.productSpecificationData?.result;
    const mediaLinks: AttributeValue[] = [];
    productSpecificationData?.map((productData: AttributeValue) => {
        if (productData?.TextValue?.startsWith('http')) {
            mediaLinks.push(productData);
        }
    });

    const linkHeaderStyle: React.CSSProperties = {
        fontFamily: `"Ensign:Sans",Arial,"noto sans" ,sans-serif`,
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '16px',
        lineHeight: '17px',
        paddingBottom: '6px'
    };

    const linkStyle: React.CSSProperties = {
        fontFamily: `"Ensign:Sans",Arial,"noto sans" ,sans-serif`,
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '16px',
        borderTop: '1px solid #BDC0C0',
        paddingLeft: '17px'
    };

    let header = props.resources.otherOptionsText;
    const amountOfLinks = mediaLinks.length;
    if (amountOfLinks > 1) {
        header = `${props.resources.otherOptionsText}(1-${amountOfLinks} of ${amountOfLinks})`;
    }

    return (
        <>
            {mediaLinks.length > 0 ? (
                <div style={{ margin: '48px 0' }}>
                    <div style={linkHeaderStyle}>{header}</div>
                    {mediaLinks.map((link: any, index: number) =>
                        link.TextValue ? (
                            <div style={linkStyle} key={index}>
                                <a href={link.TextValue} style={{ height: '48px', lineHeight: '48px' }}>
                                    <div style={{ marginTop: '3px' }}>
                                        <ExternalLink size='1.5rem' />
                                    </div>
                                    <div style={{ paddingLeft: '7px' }}>{link.Name}</div>
                                </a>
                            </div>
                        ) : (
                            ''
                        )
                    )}
                </div>
            ) : (
                ''
            )}
        </>
    );
};

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps, resources: any): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;
    // @ts-ignore
    const test = _.isEmpty(errorBlock.props.configureErrors);
    // @ts-ignore
    const addToCartError = errorBlock.props.configureErrors[5] ? true : false;

    return (
        <Node {...ContainerProps}>
            {/* {errorBlock} */}
            {errorBlock && !test && addToCartError && (
                <Error style={{ fontSize: '18px', lineHeight: '24px', fontWeight: '300', marginTop: '10px' }}>
                    <div>
                        {/* @ts-ignore */}
                        {_.map(errorBlock.props.configureErrors, (value, key) => {
                            if (key) {
                                return <div>{value}</div>;
                            }
                            return;
                        })}
                    </div>
                </Error>
            )}
            {button}
        </Node>
    );
};

const _renderAddToOrderTemplate = (addToOrderTemplate: IBuyboxAddToOrderTemplateViewProps, resources: any): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToOrderTemplate;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const isPurchaseEnabled = (productSpecificationData: AttributeValue[] | undefined) => {
    const findViewOnly = productSpecificationData?.filter(spec => {
        return spec.ExtensionProperties?.find((y: any) => y.Key === 'AttributeType')?.Value?.IntegerValue === 9;
    });
    if (findViewOnly && findViewOnly[0] && findViewOnly[0].TextValue && findViewOnly[0].TextValue === 'ViewOnly_YES') {
        return false;
    } else if (findViewOnly && findViewOnly[0] && findViewOnly[0].TextValue && findViewOnly[0].TextValue === 'ViewOnly_Yes') {
        return false;
    } else {
        return true;
    }
};

const _renderConfigure = (
    configure: IBuyboxProductConfigureViewProps,
    productSpecificationData: AttributeValue[] | undefined,
    resources: ILdsBuyboxResources,
    sizingChart: boolean,
    count: any,
    setCount: any
): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    const dimStyle = productSpecificationData?.find(
        x => x.ExtensionProperties?.find((y: any) => y.Key === 'AttributeType')?.Value?.IntegerValue === 5
    );
    const dimSize = productSpecificationData?.find(
        x => x.ExtensionProperties?.find((y: any) => y.Key === 'AttributeType')?.Value?.IntegerValue === 6
    );
    const dimColor = productSpecificationData?.find(
        x => x.ExtensionProperties?.find((y: any) => y.Key === 'AttributeType')?.Value?.IntegerValue === 7
    );
    const dimConfiguration = productSpecificationData?.find(
        x => x.ExtensionProperties?.find((y: any) => y.Key === 'AttributeType')?.Value?.IntegerValue === 8
    );
    dropdowns.sort((a, b) => {
        //@ts-ignore
        return a.select.props.dropdownId === '4' ? -1 : b.select.props.dropdownId === '4' ? -1 : 0;
    });

    dropdowns.map(x => {
        // @ts-ignore
        if (x?.select?.props.dropdownId === '1') {
            if (dimColor?.TextValue) {
                const headingCopy = React.cloneElement(x.heading as React.ReactElement, { children: dimColor.TextValue });
                const selectCopy = React.cloneElement(x.select as React.ReactElement, {
                    dropdownToggleName: `${formatString(resources.selectDimensionFormatString, [dimColor.TextValue])}`,
                    dropdownName: dimColor.TextValue
                });
                x.heading = headingCopy;
                x.select = selectCopy;
            }
        }
        // @ts-ignore
        if (x?.select?.props.dropdownId === '3') {
            if (dimSize?.TextValue) {
                const headingCopy = React.cloneElement(x.heading as React.ReactElement, { children: dimSize.TextValue });
                const selectCopy = React.cloneElement(x.select as React.ReactElement, {
                    dropdownToggleName: `${formatString(resources.selectDimensionFormatString, [dimSize.TextValue])}`
                });
                x.heading = headingCopy;
                x.select = selectCopy;
            }
        }
        // @ts-ignore
        if (x?.select?.props.dropdownId === '4') {
            if (dimStyle?.TextValue) {
                const headingCopy = React.cloneElement(x.heading as React.ReactElement, { children: dimStyle.TextValue });
                const selectCopy = React.cloneElement(x.select as React.ReactElement, {
                    dropdownToggleName: `${formatString(resources.selectDimensionFormatString, [dimStyle.TextValue])}`
                });
                x.heading = headingCopy;
                x.select = selectCopy;
            }
        }
        // @ts-ignore
        if (x?.select?.props.dropdownId === '2') {
            if (dimConfiguration?.TextValue) {
                const headingCopy = React.cloneElement(x.heading as React.ReactElement, { children: dimConfiguration.TextValue });
                const selectCopy = React.cloneElement(x.select as React.ReactElement, {
                    dropdownToggleName: `${formatString(resources.selectDimensionFormatString, [dimConfiguration.TextValue])}`
                });
                x.heading = headingCopy;
                x.select = selectCopy;
            }
        }
        return x;
    });

    return (
        <Node {...ContainerProps}>
            {dropdowns.map((dropdown: IBuyboxProductConfigureDropdownViewProps) => {
                return _renderConfigureDropdown(dropdown, resources, sizingChart, count, setCount);
            })}
        </Node>
    );
};

const _renderConfigureDropdown = (
    dropdown: IBuyboxProductConfigureDropdownViewProps,
    resources: any,
    sizingChart: boolean,
    count: any,
    setCount: any
): JSX.Element => {
    let isSizeDropdown = false;
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;
    // @ts-ignore
    if (select && select.props.dropdownList.length === 1) {
        // @ts-ignore
        select.props.dropdownList[0].isDefault = true;
    }
    // @ts-ignore
    if (dropdown.heading.props.children === resources.productDimensionTypeSize) {
        isSizeDropdown = true;
    }

    if (isSizeDropdown && sizingChart) {
        const incrementQueryParam = () => {
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            const newCount = count + 1;

            // Update the URL with the new count
            const params = new URLSearchParams(window.location.search);
            params.set('show', newCount);

            // Update the URL without reloading the page
            window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);

            // Update the state with the new count
            setCount(newCount);
        };
        return (
            <Node {...ContainerProps}>
                <Node {...LabelContainerProps}>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        {heading}
                        <a
                            style={{ marginLeft: '10px', borderTop: '0px', paddingTop: '0px' }}
                            href='#sizingChart'
                            onClick={incrementQueryParam}
                        >
                            {resources.sizingChartLabel}
                        </a>
                    </div>
                    {/* {errors} */}
                    {/* @ts-ignore */}
                    {errors && <Error style={{ paddingBottom: '5px' }}>{errors.props.children[1].props.children}</Error>}
                </Node>
                {select}
            </Node>
        );
    } else {
        return (
            <Node {...ContainerProps}>
                <Node {...LabelContainerProps}>
                    {heading}
                    {/* {errors} */}
                    {/* @ts-ignore */}
                    {errors && <Error style={{ paddingBottom: '5px' }}>{errors.props.children[1].props.children}</Error>}
                </Node>
                {select}
            </Node>
        );
    }
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, errors, button, modal } = findInStore;

    return (
        <Node {...ContainerProps}>
            {/* @ts-ignore */}
            {findInStore.addToCartValidationError}
            {/* @ts-ignore */}
            {/* {findInStore.addToCartQuantityError} */}
            {storeSelector}
            {heading}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

const _renderQuantity = (
    quantity: IBuyboxProductQuantityViewProps,
    setQuantityError?: any,
    errorOpen?: any,
    setErrorOpen?: any,
    buyboxQuantityError?: any
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {/* {errors} */}
            </Node>
            {input}
            {/* {props.state.quantityError && <Error style={{ marginTop: '5px' }}>{format(props.resources.maxQuantityError, props.max)}</Error>} */}
            {/* @ts-ignore */}
            {setQuantityError ? setQuantityError : buyboxQuantityError ? buyboxQuantityError : null}
            {/* {buyboxQuantityError} */}
            {errors && errorOpen && (
                <Error open={errorOpen} onClose={() => setErrorOpen(false)} style={{ marginTop: '5px' }}>
                    {/* @ts-ignore */}
                    {errors.props.children[1].props.children}
                </Error>
            )}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};
export default BuyboxView;
